/**
 * NewVersionBanner - Detects when a new server version is deployed and prompts users to refresh.
 *
 * Behavior:
 * - Polls /health/ every 60 seconds to check server commit SHA
 * - First poll establishes a baseline; subsequent polls detect changes from that baseline
 * - If user active (interaction within 10 min): shows dismissible banner
 * - If user idle (10+ minutes): auto-refreshes the page
 * - Pauses polling when tab is hidden
 * - Only enabled when VITE_BUILD_VERSION is set (production builds)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { AlertFullWidth } from '@/lib/base/overlays/alerts';
import { getBuildVersion } from '@/lib/config';
import { fetchWithDefaults } from '@/lib/http/client';

interface HealthResponse {
  status: string;
  commit: string;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds
const IDLE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
const ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart'] as const;

export function NewVersionBanner() {
  // State
  const [showBanner, setShowBanner] = useState(false);

  // Refs
  const baselineVersionRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const isPollingRef = useRef(false);
  const isDismissedRef = useRef(false);

  // Get build version (baked in at build time)
  const buildVersion = getBuildVersion();

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Check if user is idle
  const isUserIdle = useCallback(() => {
    return Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS;
  }, []);

  // Poll for version
  const checkVersion = useCallback(async () => {
    if (!buildVersion || isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetchWithDefaults('/health/', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok || !isMountedRef.current) {
        return;
      }

      const data: HealthResponse = await response.json();
      const serverVersion = data.commit;

      // Skip if server version is unknown
      if (serverVersion === 'unknown') {
        return;
      }

      // First poll establishes baseline - just record and return
      if (baselineVersionRef.current === null) {
        baselineVersionRef.current = serverVersion;
        return;
      }

      // Detect server-side deploy: server version changed from baseline
      if (serverVersion !== baselineVersionRef.current) {
        if (isUserIdle()) {
          // Auto-refresh for idle users
          window.location.reload();
        } else {
          // Show banner for active users (if not dismissed)
          if (isMountedRef.current && !isDismissedRef.current) {
            setShowBanner(true);
          }
        }
      }
    } catch (error) {
      // Log and continue - don't show banner on network errors
      // Silently ignore AbortError (expected on timeout)
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error('[NewVersionBanner] Health check failed:', error);
      }
    } finally {
      clearTimeout(timeoutId);
      isPollingRef.current = false;
    }
  }, [buildVersion, isUserIdle]);

  // Schedule next poll
  const scheduleNextPoll = useCallback(() => {
    if (!isMountedRef.current || document.hidden) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Reset dismissed state on new poll cycle - allow banner to re-appear
      isDismissedRef.current = false;
      checkVersion().then(scheduleNextPoll);
    }, POLL_INTERVAL_MS);
  }, [checkVersion]);

  // Main polling effect
  useEffect(() => {
    // Skip if no build version (development mode)
    if (!buildVersion) {
      return;
    }

    isMountedRef.current = true;

    // Initial check
    void checkVersion();

    // Schedule recurring polls after a short delay to avoid runOnlyPendingTimersAsync
    setTimeout(() => {
      if (isMountedRef.current) {
        scheduleNextPoll();
      }
    }, 0);

    // Visibility change handler
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause polling
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        // Resume with immediate check
        void checkVersion();
        setTimeout(() => {
          if (isMountedRef.current) {
            scheduleNextPoll();
          }
        }, 0);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [buildVersion, checkVersion, scheduleNextPoll]);

  // Handle dismiss
  const handleDismiss = () => {
    setShowBanner(false);
    isDismissedRef.current = true;
  };

  // Handle refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  // Don't render if no build version or banner not visible
  if (!buildVersion || !showBanner) {
    return null;
  }

  return (
    <AlertFullWidth
      title='New version available'
      description="There's a newer version of Ahoy available. Refresh to get the latest features."
      color='brand'
      confirmLabel='Refresh'
      dismissLabel='Dismiss'
      onConfirm={handleRefresh}
      onClose={handleDismiss}
      actionType='button'
    />
  );
}
